import { useQuery } from '@tanstack/react-query';
import { useAuthenticatedApi } from '../api';
import { useAuth0 } from '@auth0/auth0-react';

/**
 * Homepage opportunities preview. Subscription gating is handled in App.js (redirect to /plans).
 */
const fetchHomeOpportunities = async (authenticatedFetch) => {
  const response = await authenticatedFetch(
    `${process.env.REACT_APP_API_URL || '/api'}/opportunities?limit=3&brief=1`
  );

  if (!response.ok) {
    const err = new Error(`opportunities_${response.status}`);
    err.status = response.status;
    throw err;
  }

  const data = await response.json();
  return data.data || [];
};

export const useHomeOpportunities = () => {
  const { authenticatedFetch } = useAuthenticatedApi();
  const { isAuthenticated, isLoading: authLoading, user } = useAuth0();

  const queryEnabled = !authLoading && isAuthenticated && !!authenticatedFetch;

  const query = useQuery({
    queryKey: ['homeOpportunities', user?.sub ?? 'anon'],
    queryFn: () => fetchHomeOpportunities(authenticatedFetch),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    // Don't hammer the server on 403/401 (auth or entitlement mismatch)
    retry: (failureCount, error) => {
      const status = error?.status;
      if (status === 401 || status === 403) return false;
      return failureCount < 5;
    },
    retryDelay: (attemptIndex) => Math.min(15000, 500 * 2 ** attemptIndex),
    refetchOnReconnect: true,
    refetchInterval: (q) => (q.state.status === 'error' ? 10000 : false),
    enabled: queryEnabled,
  });

  const sectionLoading =
    queryEnabled && (!query.isFetched || query.isFetching || query.isError);

  return {
    ...query,
    data: query.data ?? [],
    isLoading: sectionLoading,
  };
};
